<template>
  <div>
    <!-- Floating Button -->
    <div
      v-if="!isOpen"
      @click="toggleChat"
      class="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 w-14 h-14 sm:w-16 sm:h-16 bg-primary text-primary-foreground rounded-full shadow-lg flex items-center justify-center cursor-pointer hover:bg-primary/90 transform hover:scale-105 transition-all duration-300 z-50"
    >
      <i class="fas fa-robot text-lg sm:text-2xl"></i>
    </div>

    <!-- Chat Window -->
    <div
      v-if="isOpen"
      class="fixed bottom-0 right-0 sm:bottom-6 sm:right-6 w-full sm:w-80 sm:w-96 h-[100dvh] sm:h-[520px] bg-card rounded-t-2xl sm:rounded-xl shadow-2xl flex flex-col overflow-hidden z-50 transform transition-all duration-300"
    >
      <!-- Header -->
      <div class="bg-primary text-primary-foreground p-4">
        <div class="flex items-center justify-between">
          <div class="flex items-center">
            <div
              class="w-10 h-10 bg-white rounded-full flex items-center justify-center mr-3"
            >
              <i class="fas fa-robot text-gray-900 text-lg"></i>
            </div>
            <div>
              <h3 class="font-semibold text-lg">Applypa Assistant</h3>
              <p class="text-primary-foreground/70 text-xs">{{ statusMessage }}</p>
            </div>
          </div>
          <div class="flex items-center space-x-2">
            <button
              @click="clearHistory"
              class="text-primary-foreground/70 hover:text-primary-foreground hover:bg-primary/90 rounded-md p-1.5 text-sm transition duration-200"
              title="Clear History"
              v-if="!needsAuth || isAuthenticated"
            >
              <i class="fas fa-trash-alt"></i>
            </button>
            <button
              @click="toggleChat"
              class="text-primary-foreground/70 hover:text-primary-foreground hover:bg-primary/90 rounded-md p-1.5 text-sm transition duration-200"
              title="Close"
            >
              <i class="fas fa-times"></i>
            </button>
          </div>
        </div>
      </div>

      <!-- Chat Content -->
      <div class="flex-grow flex flex-col h-full">
        <!-- Authentication Required Message -->
        <div
          v-if="needsAuth && !isAuthenticated"
          class="text-center py-8 px-6 flex-grow flex flex-col items-center justify-center"
        >
          <div
            class="w-16 h-16 bg-red-500/15 rounded-full flex items-center justify-center mx-auto mb-4"
          >
            <i class="fas fa-lock text-red-600 dark:text-red-400 text-2xl"></i>
          </div>
          <h3 class="text-lg font-semibold text-foreground mb-2">
            Authentication Required
          </h3>
          <p class="text-muted-foreground text-sm max-w-md mx-auto mb-4">
            Please log in to access the Applypa assistant.
          </p>
          <router-link
            to="/login"
            class="inline-block bg-primary text-primary-foreground px-5 py-2 rounded-lg hover:bg-primary/90 transition duration-200 text-sm"
          >
            Log In Now
          </router-link>
        </div>

        <div v-else class="flex flex-col h-full">
          <!-- Messages Container -->
          <div
            ref="messagesContainer"
            class="flex-grow p-4 overflow-y-auto scrollbar-visible min-h-[374px] pb-6"
            style="max-height: calc(100% - 60px);"
          >
            <div v-if="messages.length === 0" class="text-center py-8">
              <div
                class="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4"
              >
                <i class="fas fa-comments text-primary text-2xl"></i>
              </div>
              <h3 class="text-lg font-semibold text-foreground mb-2">
                How can I help you?
              </h3>
              <p class="text-muted-foreground text-sm max-w-md mx-auto">
                Ask me about job applications, your account, or using our
                platform.
              </p>

              <!-- Quick Suggestions -->
              <div class="flex flex-wrap justify-center gap-1.5 mt-4">
                <button
                  v-for="(suggestion, index) in quickSuggestions"
                  :key="index"
                  @click="sendMessage(suggestion)"
                  class="bg-primary/10 text-primary px-3 py-1.5 rounded-full text-xs hover:bg-primary/20 transition-colors mt-1"
                >
                  {{ suggestion }}
                </button>
              </div>
            </div>

            <div v-else>
              <div
                v-for="(message, index) in messages"
                :key="index"
                :class="[
                  'mb-5 flex',
                  message.isUser ? 'justify-end' : 'justify-start',
                ]"
              >
                <div
                  :class="[
                    'max-w-[80%] rounded-lg p-3',
                    message.isUser
                      ? 'bg-primary text-primary-foreground rounded-br-none'
                      : 'bg-muted text-foreground rounded-bl-none',
                  ]"
                >
                  <div class="flex items-start">
                    <div
                      v-if="!message.isUser"
                      class="w-6 h-6 bg-white rounded-full flex items-center justify-center mr-2 mt-0.5 flex-shrink-0"
                    >
                      <i class="fas fa-robot text-gray-900 text-xs"></i>
                    </div>
                    <div>
                      <div
                        v-if="!message.isUser"
                        class="whitespace-pre-wrap text-sm leading-relaxed"
                        v-html="renderBotMessage(message.text)"
                      ></div>
                      <p
                        v-else
                        class="whitespace-pre-wrap text-sm leading-relaxed"
                      >
                        {{ message.text }}
                      </p>
                      <span
                        :class="[
                          'text-xs block mt-1',
                          message.isUser ? 'text-primary-foreground/70' : 'text-muted-foreground',
                        ]"
                      >
                        {{ message.time }}
                      </span>
                    </div>
                    <div
                      v-if="message.isUser"
                      class="w-6 h-6 bg-primary/80 rounded-full flex items-center justify-center ml-2 mt-0.5 flex-shrink-0"
                    >
                      <i class="fas fa-user text-primary-foreground text-xs"></i>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Typing indicator when bot is "thinking" -->
              <div v-if="isTyping" class="flex justify-start mb-5">
                <div
                  class="bg-muted text-foreground rounded-lg rounded-bl-none p-3 max-w-[80%]"
                >
                  <div class="flex items-center space-x-2">
                    <div
                      class="w-6 h-6 bg-white rounded-full flex items-center justify-center mr-2 flex-shrink-0"
                    >
                      <i class="fas fa-robot text-gray-900 text-xs"></i>
                    </div>
                    <div class="flex space-x-1">
                      <div class="typing-dot"></div>
                      <div class="typing-dot"></div>
                      <div class="typing-dot"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Input Area -->
          <div
            class="border-t border-border p-2 sm:p-3 flex-shrink-0 sticky bottom-0 bg-card z-10 mt-2"
          >
            <form @submit.prevent="handleSubmit" class="flex">
              <input
                v-model="userInput"
                type="text"
                placeholder="Type your question here..."
                class="flex-grow px-3 py-2 sm:py-2.5 border border-border rounded-l-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors text-sm bg-card text-foreground placeholder-muted-foreground"
                :disabled="isTyping"
              />
              <button
                type="submit"
                :disabled="
                  isTyping ||
                  !userInput.trim()
                "
                class="bg-primary text-primary-foreground px-3 sm:px-4 py-2 sm:py-2.5 rounded-r-lg hover:bg-primary/90 transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <i class="fas fa-paper-plane text-sm"></i>
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, nextTick, onMounted, onUnmounted, watch } from "vue";
import authStore from "@/store/auth";
import { useRouter } from "vue-router";
import apiClient from "@/services/api";
import MarkdownIt from "markdown-it";

export default {
  name: "FloatingChatbot",
  setup() {
    const router = useRouter();
    const isOpen = ref(false);
    const userInput = ref("");
    const messages = ref([]);
    const isTyping = ref(false);
    const messagesContainer = ref(null);
    const isAuthenticated = computed(() => authStore.state.isLoggedIn);
    const needsAuth = ref(true); // Login required: backend chatbot endpoint now requires authentication
    const GUEST_MESSAGES_KEY = "chatGuestMessages";

    const md = new MarkdownIt({
      html: false,
      linkify: true,
      breaks: true,
    });

    const renderBotMessage = (text) => {
      const raw = String(text ?? "");
      return md.render(raw);
    };

    // Loading state flags to prevent duplicate API calls
    const isLoadingMessages = ref(false);
    const hasLoadedMessages = ref(false);

    // Debounced save timer
    let saveTimer = null;

    // Auto-save timer
    let autoSaveTimer = null;

    let saveInFlight = false;

    let lastSavedMessagesFingerprint = null;

    // Import the responses directly from HelpDeskChatbot
    const responses = {
      // Job application process
      apply:
        'To apply for a job, browse our job listings page and click on "Apply Now" on any job that interests you. You\'ll need to have your profile completed with your skills and resume uploaded. Most importantly, you must connect your job account first in your settings to enable job applications. Without that connection, you\'ll receive an application error when trying to apply. When properly connected, your applications will be tracked in the "Applications" section of your dashboard.',

      "application error":
        "The most common application error is not having your job account connected. Go to Settings > Integrations and connect your account. Make sure your profile is also complete. Other common issues include not having a resume uploaded or incomplete required fields in your profile. If you've verified all these are complete and still have issues, please contact our support team.",

      onlinejobs:
        "Account integration is required to apply for jobs on Applypa. This allows employers to view your full profile when you apply. To connect your account, go to Settings > Integrations and follow the connection process. You'll need your login credentials to authorize the connection.",

      "online jobs":
        "Applypa partners with job platforms where employers post jobs and review candidate profiles. Applypa enhances that experience by providing improved job matching, application tracking, and additional features. You need to connect your job account to apply for jobs on Applypa.",

      integration:
        'To integrate your job account with Applypa, go to Settings > Integrations and click "Connect Account". You\'ll be redirected to authorize the connection using your login credentials. This integration is essential for job applications as it allows employers to view your complete profile when reviewing your application.',

      // Profile features
      resume:
        "Your resume is essential for job applications on Applypa. To upload or update your resume, go to your Profile > Resume section. We accept PDF and DOCX formats up to 5MB. Make sure your resume highlights relevant skills and experience for the types of jobs you're applying to. Your resume is sent to employers along with your profile when you apply to jobs.",

      "why resume":
        "While Applypa integrates with your job account for your base profile, your resume provides additional details and formatting that might not be included in your account profile. It allows you to highlight specific qualifications for different job types and gives employers a comprehensive view of your capabilities. A well-crafted resume significantly increases your chances of getting interview invitations.",

      "resume format":
        "Applypa accepts resumes in PDF and DOCX formats up to 5MB. We recommend a clean, professional layout with clear section headings (e.g., Experience, Education, Skills). Include your contact information, a concise summary of your professional background, relevant experience, education, and key skills. Use bullet points for readability and keep the total length to 1-2 pages.",

      "update resume":
        'To update your resume, go to your Profile > Resume section and click "Replace Resume". Upload your updated document (PDF or DOCX format) and confirm the change. Your new resume will be used for all future applications. We recommend keeping your resume current with your latest skills, experience, and achievements.',

      // System features
      "chat limit":
        "The chatbot now supports unlimited questions. You can chat as long as you need and click the trash icon anytime to clear history.",

      "clear history":
        "You can clear your chat history anytime by clicking the trash icon in the top-right corner of the chat window. This removes previous questions and answers from the current session.",

      // AI features
      "ai cover letter":
        'Applypa offers an AI-powered cover letter generator on the job details page. When you\'re viewing a job, click the "Generate Cover Letter" button to create a customized cover letter based on your profile and the job requirements. You can edit the generated content before including it with your application. This feature helps highlight your relevant skills and experience in a professionally formatted message to employers.',

      "ai generated":
        'The "AI Generated" feature on job listings can create customized application materials tailored to specific job requirements. Using information from your profile and the job description, it can generate professional cover letters and application responses that highlight your relevant skills and experience. This helps you submit more effective applications while saving time.',

      "ai instructions":
        'The "AI Instructions" feature allows you to provide guidance to our AI tools when generating content for your applications. You can specify aspects of your experience to emphasize, particular skills to highlight, or your preferred tone and style. This ensures the AI-generated content accurately represents you while saving you time on application preparation.',

      // Plus other essential topics not included here for brevity
    };

    // Add new responses for advanced job features
    responses["advanced filters"] =
      "Premium subscribers have access to advanced job filters that include job type (full-time, part-time, contract), salary range, date posted, and skills/tags filtering. These filters appear when you click \"Show Advanced Filters\" on the jobs listing page. Using these filters helps you find exactly the kind of positions you're looking for much faster by eliminating jobs that don't match your criteria.";

    responses["advanced job filters"] =
      'Premium subscribers can filter jobs by type (full-time, part-time, contract), salary range, date posted, and specific skills or tags. On the jobs listing page, click "Show Advanced Filters" to access these options. For skill-based filtering, you can either select from popular tags or add custom tags. These filters save you significant time by showing only jobs that perfectly match your requirements.';

    responses["saved jobs"] =
      "Applypa allows you to save jobs you're interested in to review later or apply when you're ready. Saving jobs is unlimited on every plan, including Free. To save a job, simply click the bookmark icon on any job listing. You can view and manage your saved jobs from your dashboard.";

    responses["save jobs"] =
      "To save a job for later, click the bookmark icon on any job listing. Saving jobs is unlimited on every plan, including Free. You can access all your saved jobs from your dashboard.";

    responses["similar jobs"] =
      'When viewing a job, you can find similar opportunities by clicking the "View Similar Jobs" button. Our system analyzes the current job\'s title, required skills, and job type to recommend related positions. This feature helps you discover relevant opportunities you might have missed with your initial search. Similar jobs are ranked by relevance, with the most closely matching positions appearing first.';

    responses["find similar jobs"] =
      'To find jobs similar to one you\'re interested in, view the job details and click the "View Similar Jobs" button. Our system will find related jobs based on matching skills, similar job titles, and job types. This is a great way to expand your opportunities without having to create multiple search queries. The results are ranked by similarity, so the most relevant matches appear at the top.';

    responses["free trial end"] =
      "Applypa gives every new signup a 14-day free trial with full Premium access - unlimited Auto-Apply, unlimited Direct Apply, 500 AI cover letters a month, and more, no credit card required. After the 14 days, you move to the Free plan automatically (unlimited applying and 5 AI cover letters a month). Upgrade to Basic for 250 cover letters a month, or to Premium for 500 plus unlimited Auto Apply.";

    responses["trial expiration"] =
      "Your free trial lasts 14 days from signup and includes full Premium access. Once it ends, you move to the Free plan automatically and keep using Applypa with Free limits. You can upgrade to Basic or Premium anytime from the Subscription Plans page.";

    // Save messages to database with debouncing to avoid excessive API calls
    const saveMessagesDebounced = () => {
      // Clear any existing timer
      if (saveTimer) {
        clearTimeout(saveTimer);
      }

      // Set new timer
      saveTimer = setTimeout(() => {
        saveMessages();
      }, 2000);
    };

    // Status message with authentication status
    const statusMessage = computed(() => {
      if (!isAuthenticated.value && needsAuth.value) {
        return "Login Required";
      }
      if (isTyping.value) return "Typing...";
      if (messages.value.length === 0) return "Online | Ready to help";
      return "Online";
    });

    // Enhanced quick suggestions with better coverage of Applypa features
    const quickSuggestions = [
      "How do I apply for a job?",
      "Account integration",
      "Subscription plans comparison",
      "Fix application errors",
      "AI cover letter generation",
      "Upload resume format",
      "Is chat unlimited now?",
      "How do advanced job filters work?",
      "How many jobs can I save?",
      "Are there limits to job searches?",
      "How to find similar jobs?",
      "What happens when my free trial ends?",
    ];

    const loadGuestMessages = () => {
      try {
        const raw = localStorage.getItem(GUEST_MESSAGES_KEY);
        if (!raw) {
          messages.value = [];
          hasLoadedMessages.value = true;
          return false;
        }

        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) {
          messages.value = [];
          hasLoadedMessages.value = true;
          return false;
        }

        messages.value = parsed
          .filter((item) => item && typeof item === "object")
          .map((item) => ({
            text: String(item.text || ""),
            isUser: Boolean(item.isUser),
            time: String(item.time || getTime()),
          }));

        hasLoadedMessages.value = true;
        return messages.value.length > 0;
      } catch (error) {
        console.error("Error loading guest chat messages:", error);
        messages.value = [];
        hasLoadedMessages.value = true;
        return false;
      }
    };

    const saveGuestMessages = () => {
      try {
        if (messages.value.length === 0) {
          localStorage.removeItem(GUEST_MESSAGES_KEY);
          return;
        }

        const compact = messages.value
          .slice(-100)
          .map((msg) => ({
            text: String(msg.text || ""),
            isUser: Boolean(msg.isUser),
            time: String(msg.time || getTime()),
          }));

        localStorage.setItem(GUEST_MESSAGES_KEY, JSON.stringify(compact));
      } catch (error) {
        console.error("Error saving guest chat messages:", error);
      }
    };

    // Session ID for this chat
    const sessionId = ref(localStorage.getItem("chatSessionId") || null);

    // Conversation ID for grouping related messages
    const conversationId = ref(
      localStorage.getItem("chatConversationId") || null,
    );

    // Create new conversation ID
    const createNewConversation = () => {
      const timestamp = Date.now();
      const userId = authStore.state.user ? authStore.state.user.id : "guest";
      conversationId.value = `user-${userId}-conv-${timestamp}`;
      localStorage.setItem("chatConversationId", conversationId.value);
    };

    // Watch for user object changes (user ID might change even with same login state)
    watch(
      () => authStore.state.user,
      (newUser, oldUser) => {
        if (newUser?.id !== oldUser?.id) {

          // Update stored user ID
          if (newUser?.id) {
            localStorage.setItem("chatUserId", newUser.id);

            // Reload messages for the new user
            loadMessages();
          }
        }
      },
      { deep: true },
    );

    // Watch for authentication changes
    watch(
      () => authStore.state.isLoggedIn,
      async (isLoggedIn, wasLoggedIn) => {
        if (isLoggedIn !== wasLoggedIn) {
          const userId = authStore.state.user
            ? authStore.state.user.id
            : "guest";

          if (isLoggedIn) {
            // User logged in - force fetch their chat history from the database

            try {
              // Clear any existing messages first to avoid showing old data
              messages.value = [];

              // Make a direct API call without using any local IDs
              const response = await apiClient.get("/chatbot/history/");

              if (
                response.data &&
                Array.isArray(response.data.messages) &&
                response.data.messages.length > 0
              ) {
                // We have message history
                messages.value = response.data.messages;

                // Update local storage with conversation ID from server
                if (response.data.conversation_id) {
                  conversationId.value = response.data.conversation_id;
                  localStorage.setItem(
                    "chatConversationId",
                    conversationId.value,
                  );
                }

                // Update user ID in localStorage
                localStorage.setItem("chatUserId", userId);

              } else {
                // No message history - create a new conversation
                resetChatState(true);
              }

              // Force update the UI
              nextTick(() => {
                scrollToBottom();
              });
            } catch (error) {
              console.error("Error fetching user chat history:", error);
              // Create a new conversation on error
              resetChatState(true);
            }
            // Avoid duplicate GETs from route watcher / onMounted after login handling
            hasLoadedMessages.value = true;
          } else {
            // User logged out - switch to guest history
            resetChatState(true);
            loadGuestMessages();
          }
        }
      },
    );

    // Reset chat state completely
    const resetChatState = (clearMessages = true) => {

      // Only clear messages if explicitly requested
      if (clearMessages) {
        messages.value = [];
      }

      // Reset loaded flag when clearing messages
      if (clearMessages) {
        hasLoadedMessages.value = false;
      }

      // Reset session and conversation IDs
      const userId = authStore.state.user ? authStore.state.user.id : "guest";

      // Don't create new sessionId/conversationId if we're just refreshing the page
      // (clearMessages = false) and we already have valid IDs
      const shouldCreateNewIds =
        clearMessages || !sessionId.value || !conversationId.value;

      if (shouldCreateNewIds) {
        sessionId.value = `user-${userId}-session-${
          crypto.randomUUID ? crypto.randomUUID() : Date.now()
        }`;
        localStorage.setItem("chatSessionId", sessionId.value);

        // Create new conversation
        createNewConversation();
      }

      // Store current user ID
      localStorage.setItem("chatUserId", userId);

      return shouldCreateNewIds;
    };

    // Load messages from database
    const loadMessages = async () => {
      if (!isAuthenticated.value) {
        return loadGuestMessages();
      }

      // Prevent duplicate requests
      if (isLoadingMessages.value) {
        return false;
      }

      // Skip if we've already loaded messages and have data
      if (hasLoadedMessages.value && messages.value.length > 0) {
        return true;
      }

      try {
        isLoadingMessages.value = true;

        // Check if we have a token
        const token = localStorage.getItem("token");
        if (!token) {
          isLoadingMessages.value = false;
          return false;
        }

        // Get current user ID
        const userId = authStore.state.user ? authStore.state.user.id : "guest";

        // Update stored user ID
        localStorage.setItem("chatUserId", userId);

        // Build request params
        let params = {};

        // Prioritize conversation_id over session_id
        if (conversationId.value) {
          params.conversation_id = conversationId.value;
        } else if (sessionId.value) {
          params.session_id = sessionId.value;
        }

        const response = await apiClient.get("/chatbot/history/", { params });

        if (
          response.data &&
          Array.isArray(response.data.messages) &&
          response.data.messages.length > 0
        ) {
          messages.value = response.data.messages;
          hasLoadedMessages.value = true;

          // If we have a conversation_id in the response, store it
          if (response.data.conversation_id) {
            conversationId.value = response.data.conversation_id;
            localStorage.setItem("chatConversationId", conversationId.value);
          }

          isLoadingMessages.value = false;
          return true; // Indicate successful message loading
        } else {
          messages.value = [];
          hasLoadedMessages.value = true; // Mark as loaded (even if empty)

          // If we didn't get any messages and we don't have valid IDs, create new ones
          if (
            response.data.messages?.length === 0 &&
            (!conversationId.value || Object.keys(params).length === 0)
          ) {
            resetChatState(true);
          }

          isLoadingMessages.value = false;
          return false; // Indicate no messages were loaded
        }
      } catch (error) {
        console.error("Error loading chat history:", error);
        // Log more details about the error
        if (error.response) {
          console.error("Response data:", error.response.data);
          console.error("Response status:", error.response.status);
        }
        // Reset messages on error, but don't reset IDs to maintain session continuity
        messages.value = [];
        isLoadingMessages.value = false;
        return false;
      }
    };

    // Save messages to database
    const saveMessages = async () => {
      if (saveInFlight) {
        return;
      }

      if (!isAuthenticated.value) {
        saveGuestMessages();
        return;
      }

      try {
        // Check if we have a token
        const token = localStorage.getItem("token");
        if (!token) {
          return;
        }

        // Ensure messages are not empty
        if (messages.value.length === 0) {
          return;
        }

        // Get current user ID
        const userId = authStore.state.user ? authStore.state.user.id : "guest";

        // Create a conversation ID if we don't have one or ensure it has user ID
        if (!conversationId.value || !conversationId.value.includes(userId)) {
          createNewConversation();
        }

        // Sync current user ID with local storage
        localStorage.setItem("chatUserId", userId);

        // Format messages to exactly match what backend expects
        const messagesData = messages.value.map((msg) => {
          // Ensure all required fields exist and have correct types
          return {
            text: String(msg.text || ""),
            isUser: Boolean(msg.isUser),
            time: String(msg.time || getTime()),
          };
        });

        const fingerprint = JSON.stringify({
          messages: messagesData,
          session_id: sessionId.value,
          conversation_id: conversationId.value,
        });
        if (fingerprint === lastSavedMessagesFingerprint) {
          return;
        }

        saveInFlight = true;

        const response = await apiClient.post("/chatbot/history/", {
          messages: messagesData,
          session_id: sessionId.value,
          conversation_id: conversationId.value,
        });

        // Save the session ID if it's returned
        if (response.data && response.data.session_id) {
          sessionId.value = response.data.session_id;
          localStorage.setItem("chatSessionId", sessionId.value);
        }

        // Save the conversation ID if it's returned
        if (response.data && response.data.conversation_id) {
          conversationId.value = response.data.conversation_id;
          localStorage.setItem("chatConversationId", conversationId.value);
        }

        lastSavedMessagesFingerprint = fingerprint;
      } catch (error) {
        console.error("Error saving chat history:", error);
        // Log more details about the error
        if (error.response) {
          console.error("Response data:", error.response.data);
          console.error("Response status:", error.response.status);
        }
      } finally {
        saveInFlight = false;
      }
    };

    // Get formatted time
    const getTime = () => {
      const now = new Date();
      return now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    };

    // Send a message
    const sendMessage = async (text) => {
      // Add user message with correct format
      const userMessage = {
        text: String(text),
        isUser: true,
        time: getTime(),
      };
      messages.value.push(userMessage);
      userInput.value = "";

      // Save to database after adding user message
      saveMessagesDebounced();

      // Ensure input field is visible
      ensureInputVisible();

      // Scroll to bottom
      await nextTick();
      scrollToBottom();

      // Show bot typing indicator
      isTyping.value = true;

      // Make sure we scroll to the bottom with the typing indicator
      await nextTick();
      scrollToBottom();

      try {
        await respondToMessage(text);
      } finally {
        isTyping.value = false;

        // Save to database after bot response
        saveMessagesDebounced();

        // Ensure input field is visible after response
        ensureInputVisible();

        // Scroll to bottom again after response
        setTimeout(() => {
          scrollToBottom();
        }, 100);

      }
    };

    // Handle form submission
    const handleSubmit = () => {
      if (userInput.value.trim() && !isTyping.value) {
        sendMessage(userInput.value);
      }
    };

    // Find best response from the responses object
    const findBestResponse = (message) => {
      const lowerMessage = message.toLowerCase();

      // Check for AI-generated content questions
      if (
        (lowerMessage.includes("ai") ||
          lowerMessage.includes("artificial intelligence")) &&
        (lowerMessage.includes("cover letter") ||
          lowerMessage.includes("application"))
      ) {
        return responses["ai cover letter"];
      }

      if (
        lowerMessage.includes("ai generated") ||
        (lowerMessage.includes("ai") && lowerMessage.includes("generated"))
      ) {
        return responses["ai generated"];
      }

      if (
        lowerMessage.includes("ai instructions") ||
        (lowerMessage.includes("ai") && lowerMessage.includes("instructions"))
      ) {
        return responses["ai instructions"];
      }

      // First check for specific questions about why/what/how
      if (lowerMessage.includes("why") && lowerMessage.includes("resume")) {
        return responses["why resume"];
      }

      if (
        (lowerMessage.includes("what") || lowerMessage.includes("how")) &&
        lowerMessage.includes("format") &&
        lowerMessage.includes("resume")
      ) {
        return responses["resume format"];
      }

      if (
        (lowerMessage.includes("how") ||
          lowerMessage.includes("update") ||
          lowerMessage.includes("change")) &&
        lowerMessage.includes("resume")
      ) {
        return responses["update resume"];
      }

      if (
        (lowerMessage.includes("best") ||
          lowerMessage.includes("good") ||
          lowerMessage.includes("effective")) &&
        lowerMessage.includes("resume")
      ) {
        return responses["best resume"];
      }

      if (
        lowerMessage.includes("ats") ||
        (lowerMessage.includes("applicant") &&
          lowerMessage.includes("tracking"))
      ) {
        return responses["ats"];
      }

      if (
        (lowerMessage.includes("keywords") ||
          lowerMessage.includes("key words")) &&
        (lowerMessage.includes("resume") || lowerMessage.includes("profile"))
      ) {
        return responses["keywords"];
      }

      if (
        (lowerMessage.includes("picture") ||
          lowerMessage.includes("photo") ||
          lowerMessage.includes("image")) &&
        (lowerMessage.includes("profile") || lowerMessage.includes("account"))
      ) {
        return responses["profile picture"];
      }

      if (
        lowerMessage.includes("cover letter") ||
        (lowerMessage.includes("cover") && lowerMessage.includes("letter"))
      ) {
        return responses["cover letter"];
      }

      if (
        lowerMessage.includes("reference") ||
        lowerMessage.includes("recommendation")
      ) {
        return responses["references"];
      }

      if (
        lowerMessage.includes("skills") &&
        (lowerMessage.includes("list") ||
          lowerMessage.includes("add") ||
          lowerMessage.includes("what"))
      ) {
        return responses["skills"];
      }

      if (
        (lowerMessage.includes("work history") ||
          lowerMessage.includes("job history") ||
          lowerMessage.includes("experience") ||
          lowerMessage.includes("previous job")) &&
        (lowerMessage.includes("add") ||
          lowerMessage.includes("list") ||
          lowerMessage.includes("what"))
      ) {
        return responses["job history"];
      }

      if (
        lowerMessage.includes("certification") ||
        lowerMessage.includes("certificate")
      ) {
        return responses["certification"];
      }

      if (
        lowerMessage.includes("education") ||
        lowerMessage.includes("degree") ||
        lowerMessage.includes("school") ||
        lowerMessage.includes("university")
      ) {
        return responses["education"];
      }

      if (
        lowerMessage.includes("linkedin") ||
        lowerMessage.includes("linked in")
      ) {
        return responses["linkedin"];
      }

      if (
        lowerMessage.includes("portfolio") ||
        lowerMessage.includes("work sample")
      ) {
        return responses["portfolio"];
      }

      if (
        (lowerMessage.includes("apply") &&
          lowerMessage.includes("without") &&
          (lowerMessage.includes("olj") ||
            lowerMessage.includes("onlinejobs"))) ||
        (lowerMessage.includes("olj") && lowerMessage.includes("required"))
      ) {
        return responses["apply without olj"];
      }

      if (
        (lowerMessage.includes("job alert") ||
          lowerMessage.includes("notification")) &&
        (lowerMessage.includes("get") ||
          lowerMessage.includes("setup") ||
          lowerMessage.includes("set up"))
      ) {
        return responses["job alerts"];
      }

      if (
        lowerMessage.includes("rejected") ||
        (lowerMessage.includes("application") &&
          lowerMessage.includes("denied"))
      ) {
        return responses["rejected"];
      }

      if (
        lowerMessage.includes("interview") &&
        (lowerMessage.includes("prepare") ||
          lowerMessage.includes("tips") ||
          lowerMessage.includes("how"))
      ) {
        return responses["interview"];
      }

      if (
        (lowerMessage.includes("profile") && lowerMessage.includes("tips")) ||
        (lowerMessage.includes("improve") &&
          lowerMessage.includes("profile")) ||
        (lowerMessage.includes("better") && lowerMessage.includes("profile"))
      ) {
        return responses["profile tips"];
      }

      if (
        lowerMessage.includes("salary") ||
        lowerMessage.includes("pay") ||
        lowerMessage.includes("compensation")
      ) {
        return responses["salary"];
      }

      if (
        lowerMessage.includes("remote") ||
        lowerMessage.includes("work from home") ||
        lowerMessage.includes("wfh") ||
        lowerMessage.includes("virtual")
      ) {
        return responses["remote"];
      }

      if (
        (lowerMessage.includes("save") && lowerMessage.includes("job")) ||
        lowerMessage.includes("bookmark")
      ) {
        return responses["save jobs"];
      }

      if (
        (lowerMessage.includes("is") ||
          lowerMessage.includes("are") ||
          lowerMessage.includes("was")) &&
        lowerMessage.includes("ai") &&
        (lowerMessage.includes("chat") ||
          lowerMessage.includes("chatbot") ||
          lowerMessage.includes("assistant"))
      ) {
        return "Yes, this chat is AI. I'm Applypa Assistant, and I can help with jobs, profile setup, applications, and account questions. Is that what you were looking for?";
      }

      // Next check for key phrases
      const keyPhrases = {
        "how do i apply": "apply",
        "apply for job": "apply",
        "application process": "apply",
        "online jobs": "online jobs",
        "onlinejobs.ph": "onlinejobs",
        integration: "integration",
        connection: "integration",
        "application error": "application error",
        "why need resume": "why resume",
      };

      // Check for key phrases first
      for (const [phrase, responseKey] of Object.entries(keyPhrases)) {
        if (lowerMessage.includes(phrase)) {
          return responses[responseKey];
        }
      }

      // If no key phrase match, check for individual keywords
      for (const [keyword, reply] of Object.entries(responses)) {
        if (lowerMessage.includes(keyword)) {
          return reply;
        }
      }

      return null;
    };

    // Bot response logic
    const respondToMessage = async (message) => {
      try {
        const history = messages.value.slice(-12);
        const response = await apiClient.post("/chatbot/respond/", {
          messages: history,
        });

        const replyText = response?.data?.reply;
        if (replyText) {
          messages.value.push({
            text: String(replyText),
            isUser: false,
            time: getTime(),
          });
          return;
        }
      } catch (error) {
        console.error("Error generating AI chatbot response:", error);
      }

      const lowerMessage = message.toLowerCase();

      // Try to find the best matching response
      let response = findBestResponse(lowerMessage);

      // Format response for better readability - replace dash or asterisk bullet points with proper HTML
      if (response) {
        // Convert markdown-style bullet points to HTML list for better rendering
        if (
          response.includes("\n•") ||
          response.includes("\n-") ||
          response.includes("\n*")
        ) {
          response = response.replace(/\n([•\-*]) /g, "\n• ");
        }
      }

      // Default responses if no match found
      if (!response) {
        // Greeting detection
        if (
          lowerMessage.includes("hello") ||
          lowerMessage.includes("hi") ||
          lowerMessage.includes("hey") ||
          lowerMessage.includes("greetings")
        ) {
          response =
            "Hello! How can I help you today with Applypa? You can ask me about job applications, account integration, or resume optimization.";
        }
        // Gratitude detection
        else if (
          lowerMessage.includes("thank") ||
          lowerMessage.includes("appreciate") ||
          lowerMessage.includes("helpful")
        ) {
          response =
            "You're welcome! Is there anything else you need help with regarding your job search, applications, or Applypa features?";
        }
        // Farewell detection
        else if (
          lowerMessage.includes("bye") ||
          lowerMessage.includes("goodbye") ||
          lowerMessage.includes("see you") ||
          lowerMessage.includes("talk later")
        ) {
          response =
            "Goodbye! Feel free to come back if you have more questions about Applypa. Good luck with your job search!";
        }
        // Help request detection
        else if (
          lowerMessage.includes("help") ||
          lowerMessage.includes("assist") ||
          lowerMessage.includes("support")
        ) {
          response =
            "I can help with many topics including:\n\n• Job applications and search tips\n• Setting up your profile and resume\n• Account integration\n• Subscription plans and features\n• Account settings and preferences\n\nWhat specific area do you need assistance with?";
        }
        // Resume-related detection
        else if (
          lowerMessage.includes("resume") ||
          lowerMessage.includes("cv")
        ) {
          response =
            "Your resume is crucial for successful job applications on Applypa. You can upload your resume in your profile section, and I recommend keeping it updated with your latest skills and experience. Would you like specific advice about resume formatting, content, or how to make it stand out to employers?";
        }
        // Profile-related detection
        else if (lowerMessage.includes("profile")) {
          response =
            "Your Applypa profile showcases your professional information to potential employers. A complete profile significantly increases your chances of getting noticed. Would you like tips on optimizing your profile, adding specific sections, or understanding how employers view your information?";
        }
        // Job application detection
        else if (
          lowerMessage.includes("apply") ||
          lowerMessage.includes("application")
        ) {
          response =
            "To apply for jobs on Applypa, you'll need to connect your job account first and have your profile completed with a resume uploaded. Would you like specific information about the application process, troubleshooting application errors, or tracking your applications?";
        }
        // General fallback response
        else {
          response =
            "I'm not sure I understand your question. To help you better, could you try asking about specific features like:\n\n• Job applications and searching\n• Resume and profile optimization\n• Account integration\n• Subscription plans\n• Account settings";
        }
      }

      // Add bot message
      messages.value.push({
        text: String(response),
        isUser: false,
        time: getTime(),
      });
    };

    // Clear chat history
    const clearHistory = async () => {
      try {
        // Clear chat history in database
        if (isAuthenticated.value) {
          // Check if we have a token
          const token = localStorage.getItem("token");
          if (!token) {
            return;
          }

          // Include the appropriate ID for deletion
          let config = {};
          if (conversationId.value) {
            config = { params: { conversation_id: conversationId.value } };
          } else if (sessionId.value) {
            config = { params: { session_id: sessionId.value } };
          }


          // First clear message list so user sees immediate feedback
          messages.value = [];
          hasLoadedMessages.value = false; // Reset loaded flag

          // Then delete from database
          await apiClient.delete("/chatbot/history/", config);

          // Generate new IDs after clearing
          resetChatState(true);

        } else {
          // Just clear the frontend messages for unauthenticated users
          messages.value = [];
          hasLoadedMessages.value = false; // Reset loaded flag
          localStorage.removeItem(GUEST_MESSAGES_KEY);
          resetChatState(true);

        }

        // Also clear any pending save operations
        if (saveTimer) {
          clearTimeout(saveTimer);
          saveTimer = null;
        }

        lastSavedMessagesFingerprint = null;

        // Ensure input field is visible
        ensureInputVisible();

        // Scroll to bottom
        nextTick(() => {
          scrollToBottom();
        });
      } catch (error) {
        console.error("Error clearing chat history:", error);
        // Log more details about the error
        if (error.response) {
          console.error("Response data:", error.response.data);
          console.error("Response status:", error.response.status);
        }

        // Still reset local state even if the backend call fails
        resetChatState(true);
      }
    };

    // Scroll to bottom of messages
    const scrollToBottom = () => {
      if (messagesContainer.value) {
        setTimeout(() => {
          // Add extra padding to ensure last message is fully visible
          messagesContainer.value.scrollTop =
            messagesContainer.value.scrollHeight + 60;
        }, 50);
      }
    };

    // Ensure input field is visible
    const ensureInputVisible = () => {
      // Force layout recalculation to ensure input is in view
      if (messagesContainer.value) {
        const chatContainer = messagesContainer.value.parentElement;
        if (chatContainer) {
          // Ensure the container is properly sized
          chatContainer.style.display = "flex";
          chatContainer.style.flexDirection = "column";

          // Recalculate scrolling
          scrollToBottom();
        }
      }
    };

    // Toggle chat open/closed with save on close
    const toggleChat = async () => {
      // If chat is open and about to be closed, save the messages
      if (isOpen.value) {
        // Use direct save to ensure latest state is saved
        if (saveTimer) {
          clearTimeout(saveTimer);
          saveTimer = null;
        }
        saveMessages();
      } else {
        // Chat is about to be opened
        // If we have no messages and haven't loaded yet, try to load now.
        if (messages.value.length === 0 && !hasLoadedMessages.value) {
          await loadMessages();
        }
      }

      isOpen.value = !isOpen.value;

      // If opening the chat and we're authenticated, scroll to bottom after rendering
      if (isOpen.value && (!needsAuth.value || isAuthenticated.value)) {
        nextTick(() => {
          scrollToBottom();
        });
      }
    };

    // Check if we need to reload messages (simplified - now uses loadMessages with guards)
    const checkAndReloadMessages = async () => {
      // Skip if already loaded or loading
      if (hasLoadedMessages.value || isLoadingMessages.value) {
        return;
      }

      // Reload when empty regardless of auth mode (server for users, local for guests)
      if (messages.value.length === 0) {
        await loadMessages();
      }
    };

    // Auth + history loading is handled by the isLoggedIn watcher above (no duplicate watch).

    // Watch for route changes - only check if messages empty and not loaded
    watch(
      () => router.currentRoute.value.fullPath,
      () => {
        // When route changes, only reload if we haven't loaded yet
        if (!hasLoadedMessages.value && messages.value.length === 0) {
          loadMessages();
        }

        // If chat is open, make sure scrolling is correct
        if (isOpen.value) {
          nextTick(() => {
            scrollToBottom();
          });
        }
      },
    );

    // Initial setup
    onMounted(async () => {
      // Initialize session IDs then load messages (server for users, local for guests)
      if (!sessionId.value || !conversationId.value) {
        resetChatState(false);
      }
      await loadMessages();

      // Add event listener for beforeunload to save messages before page reloads
      window.addEventListener("beforeunload", saveMessagesBeforeUnload);

      // Add event listener for visibilitychange to save messages when tab is switched
      document.addEventListener("visibilitychange", handleVisibilityChange);

      // Set up auto-save timer (save every 30 seconds if there are messages)
      autoSaveTimer = setInterval(() => {
        if (messages.value.length > 0) {
          saveMessages();
        }
      }, 30000); // 30 seconds

      // Scroll to bottom after messages load
      nextTick(() => {
        scrollToBottom();
      });
    });

    // Cleanup event listeners when component is unmounted
    onUnmounted(() => {
      window.removeEventListener("beforeunload", saveMessagesBeforeUnload);
      document.removeEventListener("visibilitychange", handleVisibilityChange);

      // Clear auto-save timer
      if (autoSaveTimer) {
        clearInterval(autoSaveTimer);
        autoSaveTimer = null;
      }

      // Save messages when component is unmounted
      saveMessages();
    });

    // Handle beforeunload event
    const saveMessagesBeforeUnload = () => {
      // Don't use debounced version, directly save to ensure it's saved
      if (saveTimer) {
        clearTimeout(saveTimer);
        saveTimer = null;
      }
      saveMessages();
    };

    // Handle visibility change
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        // When tab is hidden, use direct save to ensure state is preserved
        if (saveTimer) {
          clearTimeout(saveTimer);
          saveTimer = null;
        }
        saveMessages();
      }
    };

    return {
      isOpen,
      toggleChat,
      userInput,
      messages,
      isTyping,
      messagesContainer,
      statusMessage,
      quickSuggestions,
      handleSubmit,
      sendMessage,
      clearHistory,
      isAuthenticated,
      needsAuth,
      sessionId,
      conversationId,
      ensureInputVisible,
      resetChatState,
      checkAndReloadMessages,
      renderBotMessage,
    };
  },
};
</script>

<style scoped>
.typing-dot {
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: #718096;
  animation: typing 1.4s infinite ease-in-out both;
}

.typing-dot:nth-child(1) {
  animation-delay: 0s;
}

.typing-dot:nth-child(2) {
  animation-delay: 0.2s;
}

.typing-dot:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes typing {
  0%,
  80%,
  100% {
    transform: scale(0.7);
    opacity: 0.5;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

/* Button and window transitions */
.fixed {
  transition: all 0.3s ease-in-out;
}

/* Chatbot button bounce animation */
@keyframes subtle-bounce {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-5px);
  }
}

/* Add a subtle animation to the chatbot button when it first appears */
.fixed.bottom-6.right-6.w-16.h-16 {
  animation: subtle-bounce 2s ease-in-out infinite;
}

.fixed.bottom-6.right-6.w-16.h-16:hover {
  animation: none;
}

/* Custom scrollbar styling */
.scrollbar-visible::-webkit-scrollbar {
  width: 8px;
}

.scrollbar-visible::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.scrollbar-visible::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 10px;
}

.scrollbar-visible::-webkit-scrollbar-thumb:hover {
  background: #a1a1a1;
}

/* Ensure Firefox has scrollbars too */
.scrollbar-visible {
  scrollbar-width: thin;
  scrollbar-color: #c1c1c1 #f1f1f1;
  padding-bottom: 30px !important;
}

/* Add extra space at the bottom of the message container for better visibility */
.scrollbar-visible:after {
  content: "";
  display: block;
  height: 20px;
  width: 100%;
}

/* Add proper spacing for bullet points in the chat */
.whitespace-pre-wrap ul,
.whitespace-pre-wrap ol {
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
  padding-left: 1.5rem;
}

.whitespace-pre-wrap li {
  margin-bottom: 0.25rem;
}

.whitespace-pre-wrap :deep(pre) {
  background: #0b1021;
  color: #e5e7eb;
  padding: 0.75rem;
  border-radius: 0.5rem;
  overflow-x: auto;
  font-size: 0.875rem;
}

.whitespace-pre-wrap :deep(code) {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
    "Liberation Mono", "Courier New", monospace;
}

.whitespace-pre-wrap :deep(p code),
.whitespace-pre-wrap :deep(li code) {
  background: rgba(0, 0, 0, 0.06);
  padding: 0.05rem 0.25rem;
  border-radius: 0.25rem;
}

/* Ensure proper line height for messages with bullet points */
.whitespace-pre-wrap {
  line-height: 1.5;
}
</style>
